import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import InforIllustration from 'lumen/assets/images/my-otb/pms/infor.svg?raw';
import MewsIllustration from 'lumen/assets/images/my-otb/pms/mews.svg?raw';
import OracleOperaIllustration from 'lumen/assets/images/my-otb/pms/opera.svg?raw';
import ProtelIllustration from 'lumen/assets/images/my-otb/pms/protel.svg?raw';
import OracleSuite8Illustration from 'lumen/assets/images/my-otb/pms/suite8.svg?raw';
import HotelSettingsMyotbMyotbBaseController from 'lumen/controllers/hotel/settings/myotb/myotb-base';
import type { Otbselfservice, Pmsversion } from 'lumen/models';
import type HotelSettingsMyotbPmsSelectionRoute from 'lumen/routes/hotel/settings/myotb/pms-selection';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  OTHER_OPTION_ID,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import type { ModelFrom } from 'lumen/utils/routes';

const DUMMY_OTB_SELFSERVICE_ID = '-1'; // Backend sends dummy otb self-service objects when there isn't an actual one persisted (yet) in the db
const DIFFERENT_PMS_ID = -2;

const TOP_PMS_IDS = {
  INFOR_HMS: 60,
  MEWS: 55,
  ORACLE_OPERA: 1,
  ORACLE_SUITE_8: 19,
  PROTEL: 179,
};

const TOP_PMS_IDS_TO_IMAGE_HTML = {
  [TOP_PMS_IDS.INFOR_HMS]: InforIllustration,
  [TOP_PMS_IDS.MEWS]: MewsIllustration,
  [TOP_PMS_IDS.ORACLE_OPERA]: OracleOperaIllustration,
  [TOP_PMS_IDS.ORACLE_SUITE_8]: OracleSuite8Illustration,
  [TOP_PMS_IDS.PROTEL]: ProtelIllustration,
} as const;

interface PmsVersion {
  display_name?: string;
  id: number;
  label?: string;
}

interface Pms extends PmsVersion {
  version_determination_help_article: string;
  versions: PmsVersion[];
}

interface TopPms extends Pms {
  display_name: string;
  imageHtml?: string;
  isSelected: boolean;
}

export default class HotelSettingsMyotbPmsSelectionController extends HotelSettingsMyotbMyotbBaseController {
  declare model: ModelFrom<HotelSettingsMyotbPmsSelectionRoute>;

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked otherPmsName = '';
  @tracked selectedPms: Pms | null | undefined = null;
  @tracked selectedPmsVersion: PmsVersion | null | undefined = null;

  pageName = STAGES.PMS_SELECTION_PAGE;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differentPmsOption() {
    return {
      id: DIFFERENT_PMS_ID,
      display_name: this.intl.t('settings.my_otb.pms_selection.other'),
      version_determination_help_article: '',
      versions: [
        {
          id: DIFFERENT_PMS_ID,
          display_name: this.intl.t('settings.my_otb.pms_selection.other'),
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMultiplePmsVersions() {
    if (!this.selectedPms?.versions) {
      return false;
    }

    return this.selectedPms?.versions?.length > 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedDifferentPms() {
    return (
      (this.selectedPms &&
        !Object.values(TOP_PMS_IDS).includes(this.selectedPms.id)) ||
      this.hasSelectedOtherPms
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedOtherPms() {
    return this.selectedPms?.id === OTHER_OPTION_ID;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isContinueDisabled() {
    // Both a PMS and PMS version must be selected
    if (!(this.selectedPms && this.selectedPmsVersion)) {
      return true;
    }

    // If the user selected 'Other' as PMS, giving the name in the text input is required
    if (this.hasSelectedOtherPms && this.otherPmsName === '') {
      return true;
    }

    // Disable the button if the different PMS option is selected.
    if (this.selectedPms?.id === this.differentPmsOption.id) {
      return true;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otherPmsOption() {
    return {
      id: OTHER_OPTION_ID,
      label: this.intl.t('general.other'),
      display_name: this.intl.t('general.other'),
      version_determination_help_article: '',
      versions: [
        {
          id: OTHER_OPTION_ID,
          label: this.intl.t('general.other'),
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsSelectionOptions() {
    return [
      ...this.model.pmsVersions.map((version) => ({
        ...version,
        id: version.id,
        label: version.display_name,
      })),
      this.otherPmsOption,
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPmsOption() {
    if (this.selectedPms?.id === this.differentPmsOption.id) {
      return undefined;
    }

    return this.pmsSelectionOptions.find(
      (pms) => pms.id === this.selectedPms?.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPmsVersionOptions() {
    if (!this.selectedPms) {
      return [];
    }

    return this.selectedPms?.versions.map((pmsVersion) => ({
      label: pmsVersion.display_name ?? '',
      pmsVersion,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPmsVersionValue() {
    return this.selectedPmsVersionOptions?.find(
      (option) => option.pmsVersion === this.selectedPmsVersion,
    );
  }

  get topPmss(): TopPms[] {
    const topPmss = Object.values(TOP_PMS_IDS)
      .map<TopPms | null>((topPmsId) => {
        const pmsSelectionOption = this.pmsSelectionOptions.find(
          (pmsSelectionOptionsEntry) =>
            pmsSelectionOptionsEntry.id === topPmsId,
        );

        if (!pmsSelectionOption) {
          return null;
        }

        return {
          ...pmsSelectionOption,
          imageHtml: TOP_PMS_IDS_TO_IMAGE_HTML[topPmsId],
          isSelected: this.selectedPms?.id === pmsSelectionOption?.id,
        };
      })
      .filter((pms): pms is TopPms => pms !== null);

    topPmss.push({
      ...this.differentPmsOption,
      isSelected:
        this.selectedPms?.id === this.differentPmsOption.id ||
        this.hasSelectedDifferentPms,
    });

    return topPmss;
  }

  @action
  async onClickContinue(): Promise<void> {
    // If it's the first time the PMS is saved, create an otbselfservice object instead of updating
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.otbSelfService.id === DUMMY_OTB_SELFSERVICE_ID) {
      this.store.unloadRecord(this.otbSelfService);
      this.otbSelfService = this.store.createRecord<Otbselfservice>(
        'otbselfservice',
        {
          subscription: this.hotelAndMarketService.hotel,
        },
      );
    }

    // Set selected properties on the local self-service object
    if (this.hasSelectedOtherPms) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.pmsVersion = null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.otherPmsName = this.otherPmsName;
    } else if (this.selectedPmsVersion) {
      let pmsVersion = this.store.peekRecord<Pmsversion>(
        'pmsversion',
        this.selectedPmsVersion.id,
      );
      if (!pmsVersion) {
        // Create a dummy pms version record so we can set the id in the update call in the otb self-service object
        pmsVersion = this.store.createRecord<Pmsversion>('pmsversion', {
          // @ts-expect-error: Type 'number' is not assignable to type 'string'.
          id: this.selectedPmsVersion.id,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.pmsVersion = pmsVersion;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.otherPmsName = '';
    }

    // Save the self-service object
    try {
      this.isLoading = true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.otbSelfService.save();

      // Update feature flags for changed in-app states
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.access.refetchFeatureFlags();

      // Redirect based on the updated otb self-service object returned by the API in the save call
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { stage } = this.otbSelfService;

      if (
        stage === STAGES.PMS_ELIGIBILITY_QUESTIONS_PAGE ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.otbSelfService.eligibilityQuestionsAnswers
      ) {
        this.router.transitionTo('hotel.settings.myotb.pms-eligibility');
      } else if (
        // User selected an unsupported PMS.
        stage === STAGES.OTHER_SYSTEM_SELECTION_PAGE ||
        // Or user clicked on 'Change PMS' on the unsupported page and selected an unsupported PMS
        stage === STAGES.PMS_UNSUPPORTED_PAGE
      ) {
        // OTB-TODO: Use the route consts for transitions
        this.router.transitionTo('hotel.settings.myotb.other-system-selection');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (this.otbSelfService.otherSystem) {
        // User went back multiple pages and selected the same unsupported PMS as before.
        // We can tell it's the same because the other system that the user previously selected hasn't been cleared.
        // If it was a different unsupported PMS the stage would be STAGES.OTHER_SYSTEM_SELECTION_PAGE.
        this.router.transitionTo('hotel.settings.myotb.other-system-selection');
      } else {
        // User selected a supported PMS.
        this.router.transitionTo('hotel.settings.myotb.pms-setup');
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action onClickPrevious(): void {
    this.router.transitionTo('hotel.settings.myotb.intro');
  }

  @action onSelectPms(pms: Pms): void {
    this.selectedPms = pms;

    // Set PMS version
    if (this.hasMultiplePmsVersions) {
      this.selectedPmsVersion = null; // Reset version selection
    } else {
      this.selectedPmsVersion = pms.versions[0]; // If there's only one version automatically select that one
    }
  }

  @action onSelectPmsVersion(option: { pmsVersion: PmsVersion }): void {
    this.selectedPmsVersion = option.pmsVersion;
  }

  @action setDefaults(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.otbSelfService.pmsVersion) {
      // Prefill the current selected PMS and PMS version
      this.selectedPms = this.pmsSelectionOptions.find((pms) =>
        pms.versions.some(
          (pmsVersion) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            pmsVersion.id.toString() === this.otbSelfService.pmsVersion.id,
        ),
      );
      this.selectedPmsVersion = this.selectedPms?.versions.find(
        (pmsVersion) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          pmsVersion.id.toString() === this.otbSelfService.pmsVersion.id,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (this.otbSelfService.otherPmsName) {
      // If the user selected 'Other' as PMS, prefill that
      this.selectedPms = this.otherPmsOption;
      this.selectedPmsVersion = this.otherPmsOption.versions[0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.otherPmsName = this.otbSelfService.otherPmsName;
    }
  }

  @action updateOtherPmsName(event: Event): void {
    this.otherPmsName = (event.target as HTMLInputElement).value;
  }
}
